import React, { useState, useEffect, useMemo } from 'react';
import BrainLoader from './BrainLoader';
import {
  Calendar, CheckCircle, Clock, AlertCircle, TrendingUp, Upload,
  ChevronDown, ChevronUp, Building2, DollarSign, BarChart3, RefreshCw
} from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { trMilestone } from '../i18n/locale';

const API_URL = process.env.REACT_APP_API_URL || 'https://investmenter-backend-1081814658115.europe-west1.run.app';
const USD_RATE = 0.2723; // AED to USD
const EUR_RATE = 0.25;   // AED to EUR
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_DE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

interface Milestone {
  installmentNumber: number;
  description: string;
  percentage: number;
  amount: number;
  dueDate: string;
  status: 'paid' | 'pending' | 'overdue';
  paidDate?: string | null;
  receiptNumber?: string | null;
}

interface InstallmentPlan {
  id: string;
  unitId: string;
  investorId: string;
  unitName: string;
  project: string;
  unitNumber: string;
  totalPurchasePrice: number;
  currency: string;
  milestones: Milestone[];
  createdAt: string;
  updatedAt: string;
}

interface YearlySummary {
  year: number;
  months: number[];
  totalAED: number;
  totalUSD: number;
  properties: {
    unitId: string;
    unitName: string;
    project: string;
    unitNumber: string;
    months: number[];
    totalAED: number;
    totalUSD: number;
  }[];
}

interface InstallmentPlanViewProps {
  currentInvestor: any;
  units: any[];
  onUploadDocument?: (unitId: string) => void;
}

const InstallmentPlanView: React.FC<InstallmentPlanViewProps> = ({
  currentInvestor,
  units,
  onUploadDocument
}) => {
  const { t, language } = useLanguage();
  const monthLabels = language === 'de' ? MONTHS_DE : MONTHS;

  const [plans, setPlans] = useState<InstallmentPlan[]>([]);
  const [summary, setSummary] = useState<YearlySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [extracting, setExtracting] = useState<string | null>(null);
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);
  const [currency, setCurrency] = useState<'AED' | 'USD' | 'EUR'>('AED');
  const [activeView, setActiveView] = useState<'units' | 'summary'>('units');

  const investorId = currentInvestor?.id || currentInvestor?.email || '';

  useEffect(() => {
    // No investor to ask about — stop, do not sit on the spinner. `loading`
    // starts true, so without this branch a session that never resolves an
    // investor id showed "Loading payment plans..." for as long as the tab
    // stayed open, with no request ever in flight.
    if (investorId) fetchData();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investorId]);

  const fetchData = async () => {
    setLoading(true);
    setLoadError(false);
    // The backend runs at min-instances=0, so a cold container takes ~28s to
    // answer its first request (docs/DEPLOY-BACKEND-PROMPT.md, rule 7). These
    // two calls had no timeout at all: a cold start, a sleeping laptop or a
    // dropped connection all looked identical — a spinner that never ends.
    // Wait out the documented cold start, then say what happened.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 45000);
    try {
      const authToken = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      if (authToken) headers.Authorization = `Bearer ${authToken}`;

      const [plansRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/installment-plans/${encodeURIComponent(investorId)}`, { headers, signal: controller.signal }),
        fetch(`${API_URL}/api/payment-summary/${encodeURIComponent(investorId)}`, { headers, signal: controller.signal })
      ]);

      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPlans(Array.isArray(plansData) ? plansData : []);
      }
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData.summary || []);
      }
      // Neither answered — that is a failure, not an owner with no plans.
      if (!plansRes.ok && !summaryRes.ok) setLoadError(true);
    } catch (err) {
      console.error('Error fetching installment data:', err);
      setLoadError(true);
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  };

  const handleExtract = async (unitId: string) => {
    setExtracting(unitId);
    try {
      const authToken = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/installment-plans/extract/${unitId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        }
      });
      const data = await res.json();
      if (data.success) {
        await fetchData();
      }
    } catch (err) {
      console.error('Error extracting:', err);
    }
    setExtracting(null);
  };

  const updateMilestoneStatus = async (planId: string, installmentNumber: number, newStatus: 'paid' | 'pending' | 'overdue') => {
    try {
      const authToken = localStorage.getItem('authToken');
      await fetch(`${API_URL}/api/installment-plans/${planId}/milestone`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          installmentNumber,
          status: newStatus,
          paidDate: newStatus === 'paid' ? new Date().toISOString().split('T')[0] : null
        })
      });
      await fetchData();
    } catch (err) {
      console.error('Error updating milestone status:', err);
    }
  };

  const convertAmount = (aed: number): string => {
    if (currency === 'USD') return `$${Math.round(aed * USD_RATE).toLocaleString()}`;
    if (currency === 'EUR') return `€${Math.round(aed * EUR_RATE).toLocaleString()}`;
    return `AED ${Math.round(aed).toLocaleString()}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return { bg: '#e8f5e9', color: '#2e7d32', label: language === 'de' ? 'Bezahlt' : 'Paid' };
      case 'overdue': return { bg: '#fbe9e7', color: '#c62828', label: language === 'de' ? 'Überfällig' : 'Overdue' };
      default: return { bg: '#fff3e0', color: '#e65100', label: language === 'de' ? 'Ausstehend' : 'Pending' };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle size={14} color="#2e7d32" />;
      case 'overdue': return <AlertCircle size={14} color="#c62828" />;
      default: return <Clock size={14} color="#e65100" />;
    }
  };

  // Calculate overall stats
  const stats = useMemo(() => {
    let totalInvestment = 0, totalPaid = 0, totalPending = 0, totalOverdue = 0;
    plans.forEach(plan => {
      plan.milestones?.forEach(m => {
        totalInvestment += m.amount || 0;
        if (m.status === 'paid') totalPaid += m.amount || 0;
        else if (m.status === 'overdue') totalOverdue += m.amount || 0;
        else totalPending += m.amount || 0;
      });
    });
    const paidPercentage = totalInvestment > 0 ? (totalPaid / totalInvestment) * 100 : 0;
    return { totalInvestment, totalPaid, totalPending, totalOverdue, paidPercentage };
  }, [plans]);

  // Find units without installment plans
  const unitsWithoutPlans = useMemo(() => {
    const planUnitIds = new Set(plans.map(p => p.unitId));
    return units.filter(u => !planUnitIds.has(u.id));
  }, [units, plans]);

  if (loading) {
    return (
      <div style={{ padding: '24px 16px', textAlign: 'center' }}>
        <BrainLoader task="imbTaskPlans" />
        <p style={{ marginTop: 6, fontSize: 12.5, color: '#9ca3af', fontFamily: 'Montserrat, sans-serif' }}>
          {language === 'de'
            ? 'Der erste Aufruf nach einer Pause kann bis zu 30 Sekunden dauern.'
            : 'The first request after a quiet period can take up to 30 seconds.'}
        </p>
      </div>
    );
  }

  // A failed call is not an empty plan list. Rendering the normal "no plans"
  // screen here told an owner with plans on file that they had none.
  if (loadError) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', fontFamily: 'Montserrat, sans-serif' }}>
        <AlertCircle size={30} color="#D6B160" />
        <p style={{ marginTop: 12, fontWeight: 700, color: '#111827' }}>
          {language === 'de' ? 'Zahlungspläne konnten nicht geladen werden' : 'Could not load payment plans'}
        </p>
        <p style={{ marginTop: 4, fontSize: 13.5, color: '#6b7280', lineHeight: 1.5 }}>
          {language === 'de'
            ? 'Ihre Pläne sind sicher — nur die Verbindung zum Server ist fehlgeschlagen.'
            : 'Your plans are safe — this is only the app reaching the server.'}
        </p>
        <button
          onClick={fetchData}
          style={{
            marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 7,
            background: '#D6B160', border: 'none', borderRadius: 99, padding: '9px 20px',
            color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <RefreshCw size={15} /> {language === 'de' ? 'Erneut versuchen' : 'Try again'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', maxWidth: '100%' }}>
      {/* View Toggle + Currency Selector */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 4, background: '#f0f0f0', borderRadius: 12, padding: 3 }}>
          <button
            onClick={() => setActiveView('units')}
            style={{
              padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: activeView === 'units' ? '#1a1a2e' : 'transparent',
              color: activeView === 'units' ? '#fff' : '#666',
              fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: 13
            }}
          >
            <Building2 size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
            {language === 'de' ? 'Pro Einheit' : 'Per Unit'}
          </button>
          <button
            onClick={() => setActiveView('summary')}
            style={{
              padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: activeView === 'summary' ? '#1a1a2e' : 'transparent',
              color: activeView === 'summary' ? '#fff' : '#666',
              fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: 13
            }}
          >
            <BarChart3 size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
            {language === 'de' ? 'Jahresübersicht' : 'Yearly Summary'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 4, background: '#f0f0f0', borderRadius: 10, padding: 3 }}>
          {(['AED', 'USD', 'EUR'] as const).map(c => (
            <button
              key={c}
              onClick={() => setCurrency(c)}
              style={{
                padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: currency === c ? '#D6B160' : 'transparent',
                color: currency === c ? '#fff' : '#666',
                fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: 12
              }}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Overall Progress Card */}
      {plans.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%)',
          borderRadius: 20, padding: '20px', marginBottom: 16, color: '#fff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, opacity: 0.8, marginBottom: 4 }}>
                {language === 'de' ? 'Gesamtinvestition' : 'Total Investment'}
              </p>
              <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 24, fontWeight: 700 }}>
                {convertAmount(stats.totalInvestment)}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, opacity: 0.8, marginBottom: 4 }}>
                {language === 'de' ? 'Zahlungsfortschritt' : 'Payment Progress'}
              </p>
              <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 24, fontWeight: 700 }}>
                {stats.paidPercentage.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div style={{ width: '100%', background: 'rgba(255,255,255,0.15)', borderRadius: 8, height: 12, overflow: 'hidden' }}>
            <div style={{
              width: `${stats.paidPercentage}%`,
              height: '100%', borderRadius: 8,
              background: 'linear-gradient(90deg, #D6B160, #e8c785)',
              transition: 'width 0.5s ease'
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
            <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#D6B160', fontWeight: 600 }}>
              {convertAmount(stats.totalPaid)} {language === 'de' ? 'Bezahlt' : 'Paid'}
            </span>
            <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, opacity: 0.8 }}>
              {convertAmount(stats.totalPending + stats.totalOverdue)} {language === 'de' ? 'Verbleibend' : 'Remaining'}
            </span>
          </div>

          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 16 }}>
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '10px 12px' }}>
              <p style={{ fontSize: 11, opacity: 0.7, fontFamily: 'Montserrat, sans-serif' }}>{language === 'de' ? 'Bezahlt' : 'Paid'}</p>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#81c784', fontFamily: 'Montserrat, sans-serif' }}>{convertAmount(stats.totalPaid)}</p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '10px 12px' }}>
              <p style={{ fontSize: 11, opacity: 0.7, fontFamily: 'Montserrat, sans-serif' }}>{language === 'de' ? 'Ausstehend' : 'Pending'}</p>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#ffb74d', fontFamily: 'Montserrat, sans-serif' }}>{convertAmount(stats.totalPending)}</p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '10px 12px' }}>
              <p style={{ fontSize: 11, opacity: 0.7, fontFamily: 'Montserrat, sans-serif' }}>{language === 'de' ? 'Überfällig' : 'Overdue'}</p>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#ef5350', fontFamily: 'Montserrat, sans-serif' }}>{convertAmount(stats.totalOverdue)}</p>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* VIEW: PER UNIT */}
      {/* ============================================ */}
      {activeView === 'units' && (
        <div>
          {/* Units WITH installment plans */}
          {plans.map(plan => {
            const isExpanded = expandedUnit === plan.unitId;
            const planPaid = plan.milestones?.filter(m => m.status === 'paid').reduce((s, m) => s + (m.amount || 0), 0) || 0;
            const planTotal = plan.milestones?.reduce((s, m) => s + (m.amount || 0), 0) || 0;
            const planProgress = planTotal > 0 ? (planPaid / planTotal) * 100 : 0;

            return (
              <div key={plan.unitId} style={{
                background: '#fff', borderRadius: 16, marginBottom: 12,
                border: '1px solid #e8e8e8', overflow: 'hidden'
              }}>
                {/* Unit Header */}
                <div
                  onClick={() => setExpandedUnit(isExpanded ? null : plan.unitId)}
                  style={{
                    padding: '16px', cursor: 'pointer', display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 15, color: '#1a1a2e', margin: 0 }}>
                      {plan.project} – {plan.unitNumber}
                    </p>
                    <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#888', margin: '4px 0 0' }}>
                      {plan.milestones?.length || 0} {language === 'de' ? 'Raten' : 'installments'} | {convertAmount(planTotal)}
                    </p>
                    {/* Mini progress bar */}
                    <div style={{ width: '100%', maxWidth: 200, background: '#f0f0f0', borderRadius: 4, height: 6, marginTop: 8 }}>
                      <div style={{
                        width: `${planProgress}%`, height: '100%', borderRadius: 4,
                        background: planProgress >= 100 ? '#4caf50' : 'linear-gradient(90deg, #D6B160, #e8c785)'
                      }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 600,
                      color: planProgress >= 100 ? '#4caf50' : '#D6B160'
                    }}>
                      {planProgress.toFixed(0)}%
                    </span>
                    {isExpanded ? <ChevronUp size={18} color="#888" /> : <ChevronDown size={18} color="#888" />}
                  </div>
                </div>

                {/* Expanded: Milestone Table */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid #f0f0f0', padding: '0' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat, sans-serif', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#1a1a2e', color: '#fff' }}>
                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>#</th>
                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>
                              {language === 'de' ? 'Beschreibung' : 'Description'}
                            </th>
                            <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>%</th>
                            <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>
                              {language === 'de' ? 'Betrag' : 'Amount'}
                            </th>
                            <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>
                              {language === 'de' ? 'Fällig' : 'Due Date'}
                            </th>
                            <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>{t('status')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plan.milestones?.map((m, i) => {
                            const statusInfo = getStatusColor(m.status);
                            return (
                              <tr key={i} style={{ borderBottom: '1px solid #f5f5f5', background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#444' }}>{m.installmentNumber}</td>
                                <td style={{ padding: '10px 12px', color: '#333', maxWidth: 200 }}>{trMilestone(m.description)}</td>
                                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#666' }}>{m.percentage}%</td>
                                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#1a1a2e', whiteSpace: 'nowrap' }}>
                                  {convertAmount(m.amount)}
                                </td>
                                <td style={{ padding: '10px 12px', textAlign: 'center', color: '#666', whiteSpace: 'nowrap', fontSize: 12 }}>
                                  {m.dueDate ? new Date(m.dueDate).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                </td>
                                <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                  <select
                                    value={m.status}
                                    onChange={e => updateMilestoneStatus(plan.id, m.installmentNumber, e.target.value as 'paid' | 'pending' | 'overdue')}
                                    style={{
                                      background: statusInfo.bg, color: statusInfo.color,
                                      border: `1px solid ${statusInfo.color}`, borderRadius: 20,
                                      padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                      appearance: 'none', WebkitAppearance: 'none', outline: 'none'
                                    }}
                                  >
                                    <option value="pending">{t('pending')}</option>
                                    <option value="paid">{t('paid')}</option>
                                    <option value="overdue">{t('overdueLabel')}</option>
                                  </select>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#1a1a2e', color: '#fff' }}>
                            <td colSpan={2} style={{ padding: '10px 12px', fontWeight: 700, fontSize: 12 }}>
                              {language === 'de' ? 'Gesamt' : 'Total'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontSize: 12 }}>100%</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: '#D6B160' }}>
                              {convertAmount(planTotal)}
                            </td>
                            <td colSpan={2} style={{ padding: '10px 12px' }}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Units WITHOUT plans — prompt to upload */}
          {unitsWithoutPlans.map(unit => (
            <div key={unit.id} style={{
              background: '#fff', borderRadius: 16, marginBottom: 12,
              border: '1px dashed #D6B160', padding: 16
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <p style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 15, color: '#1a1a2e', margin: 0 }}>
                    {unit.project || unit.name} – {unit.unitNumber}
                  </p>
                  <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#888', margin: '4px 0 0' }}>
                    {language === 'de' ? 'Kein Zahlungsplan vorhanden' : 'No installment plan available'}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => handleExtract(unit.id)}
                    disabled={extracting === unit.id}
                    style={{
                      padding: '8px 14px', borderRadius: 10, border: '1px solid #D6B160',
                      background: 'transparent', color: '#D6B160', cursor: 'pointer',
                      fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: 12,
                      display: 'flex', alignItems: 'center', gap: 4, opacity: extracting === unit.id ? 0.5 : 1
                    }}
                  >
                    <RefreshCw size={14} style={extracting === unit.id ? { animation: 'spin 1s linear infinite' } : {}} />
                    {language === 'de' ? 'Extrahieren' : 'Extract'}
                  </button>
                  {onUploadDocument && (
                    <button
                      onClick={() => onUploadDocument(unit.id)}
                      style={{
                        padding: '8px 14px', borderRadius: 10, border: 'none',
                        background: '#D6B160', color: '#fff', cursor: 'pointer',
                        fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: 12,
                        display: 'flex', alignItems: 'center', gap: 4
                      }}
                    >
                      <Upload size={14} />
                      {language === 'de' ? 'SPA/SOA hochladen' : 'Upload SPA/SOA'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {plans.length === 0 && unitsWithoutPlans.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <DollarSign size={48} color="#ccc" style={{ marginBottom: 12 }} />
              <p style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: 16, color: '#444' }}>
                {language === 'de' ? 'Keine Immobilien gefunden' : 'No properties found'}
              </p>
              <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, color: '#888' }}>
                {language === 'de' ? 'Fügen Sie Einheiten hinzu um den Zahlungsplan zu sehen' : 'Add units to see payment schedules'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* VIEW: YEARLY SUMMARY */}
      {/* ============================================ */}
      {activeView === 'summary' && (
        <div>
          {summary.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <BarChart3 size={48} color="#ccc" style={{ marginBottom: 12 }} />
              <p style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: 16, color: '#444' }}>
                {language === 'de' ? 'Keine Zahlungsdaten' : 'No payment data'}
              </p>
              <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, color: '#888' }}>
                {language === 'de' ? 'Zahlungspläne werden angezeigt sobald Dokumente hochgeladen sind' : 'Payment plans will appear once documents are uploaded'}
              </p>
            </div>
          ) : (
            <>
              {/* Currency Toggle */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {(['AED', 'USD', 'EUR'] as const).map(c => (
                  <button key={c} onClick={() => setCurrency(c)} style={{
                    padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer',
                    fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: 13,
                    background: currency === c ? '#D6B160' : '#f0f0f0',
                    color: currency === c ? '#fff' : '#666',
                    transition: 'all 0.2s'
                  }}>
                    {c}
                  </button>
                ))}
              </div>

              {/* Bar Chart: Yearly Totals */}
              <div style={{
                background: '#fff', borderRadius: 16, border: '1px solid #e8e8e8',
                padding: 20, marginBottom: 16
              }}>
                <h3 style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 16, color: '#1a1a2e', margin: '0 0 16px' }}>
                  {language === 'de' ? 'Jährliche Zahlungen' : 'Yearly Payments'}
                </h3>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 180, padding: '0 8px' }}>
                  {summary.map((year) => {
                    const maxTotal = Math.max(...summary.map(s => s.totalAED), 1);
                    const barHeight = (year.totalAED / maxTotal) * 150;
                    return (
                      <div key={year.year} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>
                          {convertAmount(year.totalAED)}
                        </span>
                        <div style={{
                          width: '100%', maxWidth: 60, height: barHeight, minHeight: 4,
                          background: 'linear-gradient(180deg, #D6B160 0%, #b8943d 100%)',
                          borderRadius: '8px 8px 0 0',
                          transition: 'height 0.5s ease'
                        }} />
                        <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 700, color: '#444', marginTop: 6 }}>
                          {year.year}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── CURRENT/LATEST YEAR: Month rows × Unit columns (with editable status) ── */}
              {(() => {
                const maxYear = Math.max(...summary.map(s => s.year));
                const latestYear = summary.find(s => s.year === maxYear);
                if (!latestYear) return null;
                const statusCycle: Record<string, 'paid' | 'pending' | 'overdue'> = { pending: 'paid', paid: 'overdue', overdue: 'pending' };
                const statusStyle: Record<string, { bg: string; color: string; label: string }> = {
                  paid:    { bg: '#e8f5e9', color: '#2e7d32', label: `✓ ${t('paid')}` },
                  pending: { bg: '#fff8e1', color: '#f57f17', label: t('pending') },
                  overdue: { bg: '#fbe9e7', color: '#c62828', label: `⚠ ${t('overdueLabel')}` },
                };
                return (
                  <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8e8e8', overflow: 'hidden', marginBottom: 16 }}>
                    <div style={{ background: '#1a1a2e', color: '#fff', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Calendar size={16} color="#D6B160" />
                      <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 16 }}>{maxYear}</span>
                      <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#D6B160', marginLeft: 4 }}>— {t('iplTapStatusToChange')}</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat, sans-serif', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#2d3748', color: '#D6B160' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#2d3748', zIndex: 1 }}>{t('iplMonth')}</th>
                            {latestYear.properties.map(prop => (
                              <th key={prop.unitId} style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, whiteSpace: 'normal', minWidth: 130 }}>
                                {prop.project}<br /><span style={{ fontWeight: 400, color: '#D6B160' }}>{prop.unitNumber}</span>
                              </th>
                            ))}
                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{t('iplTotal')} ({currency})</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthLabels.map((monthLabel, mi) => {
                            const monthTotal = latestYear.months[mi];
                            const rowBg = mi % 2 === 0 ? '#fafafa' : '#fff';
                            return (
                              <tr key={mi} style={{ borderBottom: '1px solid #f0f0f0', background: rowBg }}>
                                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1a1a2e', whiteSpace: 'nowrap', position: 'sticky', left: 0, background: rowBg, zIndex: 1 }}>
                                  {monthLabel}
                                </td>
                                {latestYear.properties.map(prop => {
                                  const amount = prop.months[mi] || 0;
                                  const plan = plans.find(p => p.unitId === prop.unitId);
                                  const milestone = plan?.milestones.find(m => {
                                    if (!m.dueDate) return false;
                                    const d = new Date(m.dueDate);
                                    return d.getFullYear() === maxYear && d.getMonth() === mi;
                                  });
                                  const st = milestone ? (statusStyle[milestone.status] || statusStyle.pending) : null;
                                  return (
                                    <td key={prop.unitId} style={{ padding: '6px 10px', textAlign: 'center' }}>
                                      {amount > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                          <span style={{ fontWeight: 600, color: '#1a1a2e', whiteSpace: 'nowrap' }}>{convertAmount(amount)}</span>
                                          {st && plan && milestone && (
                                            <span
                                              onClick={() => updateMilestoneStatus(plan.id, milestone.installmentNumber, statusCycle[milestone.status])}
                                              style={{ background: st.bg, color: st.color, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                              title={t('iplTapToChangeStatus')}
                                            >
                                              {st.label}
                                            </span>
                                          )}
                                        </div>
                                      ) : <span style={{ color: '#ccc' }}>–</span>}
                                    </td>
                                  );
                                })}
                                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap' }}>
                                  {monthTotal > 0 ? convertAmount(monthTotal) : '–'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#D6B160', color: '#fff' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 700, position: 'sticky', left: 0, background: '#D6B160', zIndex: 1 }}>{t('iplTotal')}</td>
                            {latestYear.properties.map(prop => (
                              <td key={prop.unitId} style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {convertAmount(prop.totalAED)}
                              </td>
                            ))}
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, whiteSpace: 'nowrap', fontSize: 13 }}>
                              {convertAmount(latestYear.totalAED)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* ── PAST YEARS SUMMARY: Unit rows × Year columns ── */}
              {(() => {
                // Build summary directly from plans so ALL units are included
                const planYearMap: Record<number, Record<string, number>> = {};
                plans.forEach(plan => {
                  plan.milestones?.forEach((m: any) => {
                    if (!m.dueDate) return;
                    const yr = new Date(m.dueDate).getFullYear();
                    if (!planYearMap[yr]) planYearMap[yr] = {};
                    planYearMap[yr][plan.unitId] = (planYearMap[yr][plan.unitId] || 0) + (m.amount || 0);
                  });
                });
                const allYears = Object.keys(planYearMap).map(Number).sort();
                if (allYears.length === 0) return null;
                const maxYear = Math.max(...allYears);
                const pastYears = allYears.filter(y => y < maxYear);
                if (pastYears.length === 0) return null;
                return (
                  <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8e8e8', overflow: 'hidden', marginBottom: 16 }}>
                    <div style={{ background: '#2d3748', color: '#fff', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <BarChart3 size={16} color="#D6B160" />
                      <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 15 }}>{t('iplPastPaymentsSummary')}</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat, sans-serif', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#f5f5f5', color: '#1a1a2e' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#f5f5f5', zIndex: 1 }}>{t('unit')}</th>
                            {pastYears.map(y => <th key={y} style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{y}</th>)}
                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700 }}>{t('iplTotal')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plans.map((plan, i) => {
                            const unitTotal = pastYears.reduce((sum, y) => sum + (planYearMap[y]?.[plan.unitId] || 0), 0);
                            return (
                              <tr key={plan.unitId} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1a1a2e', whiteSpace: 'normal', minWidth: 140, position: 'sticky', left: 0, background: i % 2 === 0 ? '#fafafa' : '#fff', zIndex: 1 }}>
                                  {plan.project}<br /><span style={{ fontWeight: 400, color: '#888', fontSize: 11 }}>{plan.unitNumber}</span>
                                </td>
                                {pastYears.map(y => {
                                  const amt = planYearMap[y]?.[plan.unitId] || 0;
                                  return (
                                    <td key={y} style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap', color: amt ? '#1a1a2e' : '#ccc', fontWeight: amt ? 600 : 400 }}>
                                      {amt ? convertAmount(amt) : '–'}
                                    </td>
                                  );
                                })}
                                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#D6B160', whiteSpace: 'nowrap' }}>
                                  {convertAmount(unitTotal)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#D6B160', color: '#fff' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 700, position: 'sticky', left: 0, background: '#D6B160', zIndex: 1 }}>{t('iplTotal')}</td>
                            {pastYears.map(y => (
                              <td key={y} style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {convertAmount(Object.values(planYearMap[y] || {}).reduce((s, v) => s + v, 0))}
                              </td>
                            ))}
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, whiteSpace: 'nowrap', fontSize: 13 }}>
                              {convertAmount(pastYears.reduce((s, y) => s + Object.values(planYearMap[y] || {}).reduce((a, v) => a + v, 0), 0))}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default InstallmentPlanView;
